import { create } from "zustand";
import {
  buildWindows,
  loadDeviceHistory,
  processInBatches,
  DEVICE_CONCURRENCY,
} from "../../pages/reports/utils/trackingHistoryWindows";
import {
  backupVolts,
  nominalSystem,
  powerEpisodes,
  supplyVolts,
  type VoltEpisode,
} from "../../pages/reports/utils/voltage";
import { TEMPERATURE_COLORS } from "./reportTemperatureStore";

export interface VoltageSample {
  ts: number;
  supply: number | null;
  backup: number | null;
  ignition: boolean;
}

export interface VoltageEpisodeRow extends VoltEpisode {
  lat: number | null;
  lng: number | null;
}

export interface VoltageResult {
  deviceId: string;
  vehicleName: string;
  color: string;
  samples: VoltageSample[];
  system: 12 | 24;
  min: number | null;
  max: number | null;
  avg: number | null;
  episodes: VoltageEpisodeRow[];
  loading: boolean;
  error: string | null;
}

export interface VoltageDevice {
  deviceId: string;
  vehicleName: string;
}

interface ReportVoltageState {
  results: Record<string, VoltageResult>;
  order: string[];
  loading: boolean;
  tz: string | null;
  loadRange: (devices: VoltageDevice[], from: string, to: string, tz?: string) => Promise<void>;
  clear: () => void;
}

export const useReportVoltageStore = create<ReportVoltageState>()((set) => ({
  results: {},
  order: [],
  loading: false,
  tz: null,

  loadRange: async (devices, from, to, tz) => {
    const resolvedTz = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const windows = buildWindows(new Date(from).getTime(), new Date(to).getTime());

    const initial: Record<string, VoltageResult> = {};
    devices.forEach((d, i) => {
      initial[d.deviceId] = {
        deviceId: d.deviceId,
        vehicleName: d.vehicleName,
        color: TEMPERATURE_COLORS[i % TEMPERATURE_COLORS.length],
        samples: [],
        system: 12,
        min: null,
        max: null,
        avg: null,
        episodes: [],
        loading: true,
        error: null,
      };
    });
    set({ results: initial, order: devices.map((d) => d.deviceId), loading: true, tz: resolvedTz });

    await processInBatches(devices, DEVICE_CONCURRENCY, async (d) => {
      const { points, failedChunks } = await loadDeviceHistory(d.deviceId, windows, resolvedTz);

      const samples: VoltageSample[] = [];
      const where = new Map<number, { lat: number; lng: number }>();
      for (const p of points) {
        const supply = supplyVolts(p.params);
        const backup = backupVolts(p.params);
        if (supply == null && backup == null) continue;
        samples.push({ ts: p.ts, supply, backup, ignition: p.ignition === true });
        where.set(p.ts, { lat: p.lat, lng: p.lng });
      }
      samples.sort((a, b) => a.ts - b.ts);

      const supplyPts = samples.flatMap((s) => (s.supply == null ? [] : [{ ts: s.ts, volts: s.supply }]));
      const volts = supplyPts.map((s) => s.volts);
      const system = nominalSystem(volts);
      const episodes = powerEpisodes(supplyPts, system).map((e) => ({
        ...e,
        lat: where.get(e.start)?.lat ?? null,
        lng: where.get(e.start)?.lng ?? null,
      }));

      set((state) => {
        const current = state.results[d.deviceId];
        if (!current) return state;
        return {
          results: {
            ...state.results,
            [d.deviceId]: {
              ...current,
              samples,
              system,
              // reduce y no Math.min(...volts): 31 días a un punto cada 30 s
              // son ~90 000 argumentos y revientan la pila.
              min: volts.length ? volts.reduce((a, b) => Math.min(a, b)) : null,
              max: volts.length ? volts.reduce((a, b) => Math.max(a, b)) : null,
              avg: volts.length ? volts.reduce((a, b) => a + b, 0) / volts.length : null,
              episodes,
              loading: false,
              error:
                samples.length === 0 && failedChunks > 0
                  ? "No se pudo obtener el voltaje (servicio no disponible)."
                  : samples.length === 0
                    ? "El equipo no reporta voltaje en el rango."
                    : failedChunks > 0
                      ? `${failedChunks} tramo(s) con error`
                      : null,
            },
          },
        };
      });
    });

    set({ loading: false });
  },

  clear: () => set({ results: {}, order: [], loading: false, tz: null }),
}));
